import { useState } from 'react'
import { format } from 'date-fns'

export interface BarSeries {
  key: string
  label: string
  color: string
}

type Row = { day: string }

/**
 * Stacked daily bars on one count axis, with an optional line on the same axis (e.g. what arrived
 * each day against what was decided). Hover a day for its numbers. Dependency-free, like TrendChart.
 */
export function DailyBars({
  rows,
  series,
  line,
  height = 200,
}: {
  rows: Row[]
  series: BarSeries[]
  line?: BarSeries
  height?: number
}) {
  const [hover, setHover] = useState<number | null>(null)
  if (!rows.length) return <p className="py-8 text-center text-sm text-slate-500">Nothing decided in this window.</p>

  const W = 640
  const H = height
  const pad = { l: 44, r: 12, t: 10, b: 22 }
  const val = (r: Row, k: string) => Number((r as Record<string, unknown>)[k] ?? 0)
  const stack = (r: Row) => series.reduce((t, s) => t + val(r, s.key), 0)
  const top = Math.max(1, ...rows.map((r) => Math.max(stack(r), line ? val(r, line.key) : 0))) * 1.1
  const n = rows.length
  const slot = (W - pad.l - pad.r) / n
  const bw = Math.max(2, Math.min(28, slot - 2))
  const x = (i: number) => pad.l + i * slot + slot / 2
  const y = (v: number) => pad.t + (1 - v / top) * (H - pad.t - pad.b)
  const ticks = [0, 0.5, 1].map((f) => Math.round(f * top))
  const shown = hover ?? n - 1

  return (
    <div className="space-y-2">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" onMouseLeave={() => setHover(null)} role="img" aria-label="Daily decisions">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="#e2e8f0" />
            <text x={pad.l - 6} y={y(t) + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{t.toLocaleString()}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          let base = 0
          return (
            <g key={r.day} onMouseEnter={() => setHover(i)}>
              {/* Hit target: the whole day's column, not just the bar. */}
              <rect x={pad.l + i * slot} y={pad.t} width={slot} height={H - pad.t - pad.b} fill="transparent" />
              {series.map((s) => {
                const v = val(r, s.key)
                if (!v) return null
                const y0 = y(base)
                base += v
                const y1 = y(base)
                // 2px surface gap between stacked segments.
                return <rect key={s.key} x={x(i) - bw / 2} y={y1} width={bw} height={Math.max(0.5, y0 - y1 - 1)} rx={1.5} fill={s.color} opacity={hover == null || hover === i ? 1 : 0.45} />
              })}
            </g>
          )
        })}
        {line && (
          <polyline
            fill="none"
            stroke={line.color}
            strokeWidth="2"
            strokeDasharray="4 3"
            points={rows.map((r, i) => `${x(i)},${y(val(r, line.key))}`).join(' ')}
          />
        )}
        {[0, n - 1].filter((i, k, a) => a.indexOf(i) === k).map((i) => (
          <text key={i} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : 'end'} fontSize="10" fill="#94a3b8">
            {format(new Date(`${rows[i].day}T00:00:00`), 'MMM d')}
          </text>
        ))}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} stroke="#94a3b8" strokeDasharray="3 3" pointerEvents="none" />}
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {[...series, ...(line ? [line] : [])].map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-slate-600">
            <span className={s === line ? 'h-0.5 w-3' : 'h-2 w-2 rounded-sm'} style={{ background: s.color }} />
            {s.label}: <span className="font-medium tabular-nums">{val(rows[shown], s.key).toLocaleString()}</span>
          </span>
        ))}
        <span className="text-slate-400">on {format(new Date(`${rows[shown].day}T00:00:00`), 'MMM d')}</span>
      </div>
    </div>
  )
}
